import { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { FullscreenTakeover, QueryLoader, Button, Flex } from "@jsq/ds/core";
import { Column, ColumnContainer } from "@jsq/ds/layouts";
import { globalEnv } from "~/global-env";
import { useGetProfileDetailsQuery } from "~/graphql/supergraphHooks";
import { SPACING } from "~/styles/variables";
import { FatcaCrsDataSourceSection } from "./FatcaCrsDataSourceSection";
import { ViewProfileDetailsCard } from "./ViewProfileDetailsCard";

type Props = {
  caseId: number;
  programId: number;
};

export const ViewProfileDetailsFullscreenTakeover = ({ caseId, programId }: Props) => {
  const history = useHistory();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const profileId = Number(searchParams.get("profileId"));
  const hasProfileId = Number.isInteger(profileId) && profileId > 0;

  const profileDetailsResults = useGetProfileDetailsQuery({
    variables: {
      profileId: profileId,
      programId: programId,
    },
    skip: !hasProfileId,
    fetchPolicy: "no-cache",
  });

  const profilesUrl = `${globalEnv.complianceRoute}/case/${caseId}/profiles`;

  const onExit = () => {
    history.push(profilesUrl);
  };

  // The route matches on path, so it mounts without the param. Skipping alone
  // renders an empty takeover, so leave. replace keeps the bad URL out of history.
  useEffect(() => {
    if (!hasProfileId) {
      history.replace(profilesUrl);
    }
  }, [hasProfileId, history, profilesUrl]);

  // Skipping the query leaves QueryLoader with no data, which renders the body
  // with nothing to show. Render nothing at all while the redirect above runs.
  if (!hasProfileId) {
    return null;
  }

  const documents = profileDetailsResults.data?.complianceProfile?.documents ?? [];

  return (
    <FullscreenTakeover>
      <FullscreenTakeover.Header onExit={onExit} title="View profile" />
      <FullscreenTakeover.Body>
        <QueryLoader result={profileDetailsResults}>
          <ColumnContainer>
            <Column size="sm">
              <ViewProfileDetailsCard
                complianceProfile={profileDetailsResults.data?.complianceProfile}
                caseId={caseId}
              />
              <FatcaCrsDataSourceSection
                documents={documents}
                caseId={caseId}
                profileId={profileId}
              />
              <Flex
                css={{ marginBottom: SPACING.LARGE }}
                alignItems="flex-end"
                flexDirection="column-reverse"
              >
                <Button type="button" variant="primary" onClick={onExit}>
                  Close
                </Button>
              </Flex>
            </Column>
          </ColumnContainer>
        </QueryLoader>
      </FullscreenTakeover.Body>
    </FullscreenTakeover>
  );
};
